/**
 * @file Column-mapping surface for ONE table slice of a mapping review: a
 * vertical list of full-width rows (mirroring `MappingReviewFields`) — mapped
 * columns first, then unmapped source columns. Each mapped row keeps one
 * aligned line — a fixed-width source label, the "→" separator, the
 * target-field `MappingSelect` (already-claimed targets disabled with a "(mapped to …)"
 * suffix), then a trailing cluster of the shared `MappingReviewConfidenceTag`
 * (a manual override never hides the AI's score for the chosen target), the
 * cleaning-rule Tags, a 5px separator, and the gear / reset actions. The row
 * background tints to the warn band off the *effective* score — the AI score
 * for the chosen target when manual, else the proposal confidence — so a
 * hand-retargeted weak column keeps its risk signal. The cleaning-rules Popover
 * edits a local draft with a live blast-radius preview ("applies to N cells; M
 * couldn't be coerced") and commits only on explicit Save — outside-click / Esc
 * discards. A per-column reset-to-AI-suggestion and, for entity reference
 * targets, the resolution status line opening the `MappingReviewReferenceValues`
 * Drawer complete the row. Unmapped rows reuse the geometry with a dashed
 * border and the "not mapped" placeholder. Every editable control freezes while
 * `useMappingReviewSubmitting()` is true. Column field options come from the
 * slice's target table (`useTargetFields`); all user copy renders through
 * `mappingReviewLabels`. Reused by both the entity-import and the per-table
 * document surfaces — every mutator is keyed by the slice's group id. The grid
 * has fixed tracks, so its wrapper stretches to the panel and scrolls the grid
 * inside itself rather than widening the panel body.
 */
import { FC, useContext, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Popover,
  Select,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  LinkOutlined,
  PlusOutlined,
  SettingOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import type {
  CellCleaningRule,
  ColumnPlan,
  DateLocale,
  FieldOption,
  NumberLocale,
} from '#types';
import { coerceCellValue } from '#types';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import {
  addColumnMapping,
  removeColumnMapping,
  resetColumnToProposal,
  setColumnPercentScale,
  setColumnRules,
  setColumnTarget,
} from './mappingReviewSession';
import type { MappingReviewTableSlice } from './mappingReviewState';
import { mappingReviewConfidence } from './mappingReviewConfidence';
import { mappingReviewColumnScores } from './mappingReviewColumnScores';
import { mappingReviewLabels } from './mappingReviewLabels';
import { useMappingReviewSubmitting } from './useMappingReviewSubmitting';
import MappingReviewConfidenceTag from './MappingReviewConfidenceTag';
import MappingSelect from './MappingSelect';
import { applyCellCleaningRules } from './applyCellCleaningRules';
import { referenceKeyOccurrences } from './referenceKeyOccurrences';
import type { TargetFieldOption } from '@/services/editorExtractors';
import MappingReviewReferenceValues from './MappingReviewReferenceValues';

const { Text, Title } = Typography;

export interface ReferenceValuesTarget {
  groupId: string;
  sourceColumn: string;
  filter: 'all' | 'unresolved' | 'manual' | 'matched';
}

/** Shared grid for the column-mapping table — fixed source/confidence/cleaning/action tracks plus a flexible target track; the single `1fr` keeps every row and the header aligned to the container width. */
const COLUMN_GRID = '160px minmax(350px, 1fr) 130px 180px 40px';

const ColumnsHeader: FC = () => {
  const { token } = theme.useToken();
  const style = { fontSize: token.fontSizeSM } as const;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMN_GRID,
        columnGap: 10,
        alignItems: 'center',
        paddingInline: 5,
        paddingBottom: 5,
      }}
    >
      <Text strong style={style}>
        Source column
      </Text>
      <Text strong style={style}>
        Target field
      </Text>
      <Text strong style={style}>
        Confidence
      </Text>
      <Text strong style={style}>
        Cleaning
      </Text>
      <span />
    </div>
  );
};

const RULE_KINDS = Object.keys(
  mappingReviewLabels.rule,
) as CellCleaningRule['kind'][];

const PERCENT_SCALE_OPTIONS = [
  { value: 'percent', label: 'Already percent (0–100)' },
  { value: 'fraction', label: 'Fraction (0–1) ×100' },
] as const;

function defaultRule(kind: CellCleaningRule['kind']): CellCleaningRule {
  if (kind === 'replace') return { kind, find: '', replaceWith: '' };
  if (kind === 'coerceDate') return { kind, dateType: 'date' };
  return { kind };
}

const RulesEditor: FC<{
  sourceColumn: string;
  rules: CellCleaningRule[];
  onChange: (rules: CellCleaningRule[]) => void;
  cells: (string | number | null)[];
  locale?: NumberLocale;
  dateLocale?: DateLocale;
  centuryPivot?: number;
  dateZone?: string;
  fieldDataTypeId?: string;
  percentScale?: 'percent' | 'fraction';
  options?: FieldOption[];
}> = ({
  sourceColumn,
  rules,
  onChange,
  cells,
  locale,
  dateLocale,
  centuryPivot,
  dateZone,
  fieldDataTypeId,
  percentScale,
  options,
}) => {
  const { token } = theme.useToken();
  const move = (index: number, delta: number) => {
    const next = [...rules];
    const [rule] = next.splice(index, 1);
    next.splice(index + delta, 0, rule);
    onChange(next);
  };
  const example =
    cells.find((v) => v != null && String(v).trim() !== '') ?? null;
  const blast = useMemo(() => {
    const fieldContext = {
      ...(options ? { options } : {}),
      ...(percentScale ? { percentScale } : {}),
    };
    const hasCoerce = rules.some(
      (r) => r.kind === 'coerceNumber' || r.kind === 'coerceDate',
    );
    return cells.reduce(
      (acc, raw) => {
        if (raw == null || String(raw).trim() === '') return acc;
        const cleaned = applyCellCleaningRules(
          raw,
          rules,
          locale,
          dateLocale,
          centuryPivot,
          dateZone,
        );
        const lost =
          coerceCellValue(
            cleaned,
            fieldDataTypeId,
            { numberLocale: locale, dateLocale, centuryPivot, dateZone },
            fieldContext,
          ).value === null &&
          cleaned != null &&
          String(cleaned).trim() !== '';
        return {
          hasCoerce,
          changed: acc.changed + (cleaned !== raw ? 1 : 0),
          failed: acc.failed + (lost ? 1 : 0),
        };
      },
      { hasCoerce, changed: 0, failed: 0 },
    );
  }, [
    cells,
    rules,
    locale,
    dateLocale,
    centuryPivot,
    dateZone,
    fieldDataTypeId,
    percentScale,
    options,
  ]);

  return (
    <FlexCol gap={5}>
      {rules.map((rule, index) => (
        <FlexBox key={`${rule.kind}-${index}`} noGrow gap={5}>
          <Text style={{ fontSize: token.fontSizeSM }}>
            {index + 1}. {mappingReviewLabels.rule[rule.kind]}
          </Text>
          {rule.kind === 'replace' && (
            <>
              <Input
                size="small"
                placeholder="find"
                value={rule.find}
                aria-label={`Replace-rule find text for ${sourceColumn} rule ${index + 1}`}
                onChange={(e) => {
                  const next = [...rules];
                  next[index] = {
                    kind: 'replace',
                    find: e.target.value,
                    replaceWith: rule.replaceWith,
                  };
                  onChange(next);
                }}
              />
              <Input
                size="small"
                placeholder="with"
                value={rule.replaceWith}
                aria-label={`Replace-rule replacement text for ${sourceColumn} rule ${index + 1}`}
                onChange={(e) => {
                  const next = [...rules];
                  next[index] = {
                    kind: 'replace',
                    find: rule.find,
                    replaceWith: e.target.value,
                  };
                  onChange(next);
                }}
              />
            </>
          )}
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => move(index, -1)}
            aria-label={`Move rule ${index + 1} up for ${sourceColumn}`}
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === rules.length - 1}
            onClick={() => move(index, 1)}
            aria-label={`Move rule ${index + 1} down for ${sourceColumn}`}
          />
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => onChange(rules.filter((_, i) => i !== index))}
            aria-label={`Remove rule ${index + 1} from ${sourceColumn}`}
          />
        </FlexBox>
      ))}
      <Select
        size="small"
        placeholder="Add rule"
        value={undefined}
        aria-label={`Add cleaning rule to ${sourceColumn}`}
        options={RULE_KINDS.map((kind) => ({
          value: kind,
          label: mappingReviewLabels.rule[kind],
        }))}
        onChange={(kind: CellCleaningRule['kind']) =>
          onChange([...rules, defaultRule(kind)])
        }
      />
      {rules.length > 0 && (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Applies to {blast.changed} {blast.changed === 1 ? 'cell' : 'cells'}
          {blast.hasCoerce ? `; ${blast.failed} couldn't be coerced` : ''}
        </Text>
      )}
      {example != null && rules.length > 0 && (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          e.g. &quot;{String(example)}&quot; → &quot;
          {String(
            applyCellCleaningRules(
              example,
              rules,
              locale,
              dateLocale,
              centuryPivot,
              dateZone,
            ),
          )}
          &quot;
        </Text>
      )}
    </FlexCol>
  );
};

const ColumnRow: FC<{
  slice: MappingReviewTableSlice;
  column: ColumnPlan;
  targetFields: TargetFieldOption[];
  claimedBy: Map<string, string>;
  scores: Map<string, number> | undefined;
  onOpenReferenceValues: (target: ReferenceValuesTarget) => void;
}> = ({
  slice,
  column,
  targetFields,
  claimedBy,
  scores,
  onOpenReferenceValues,
}) => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const submitting = useMappingReviewSubmitting();
  const snap = useSnapshot(column);
  const sliceSnap = useSnapshot(slice);
  const fieldDataTypeId = fieldTypes[snap.targetField]?.fieldDataTypeId;
  const fieldOptions = fieldTypes[snap.targetField]?.props?.options;
  const isPercentage = fieldDataTypeId === 'percentage';
  const [rulesDraft, setRulesDraft] = useState<CellCleaningRule[] | null>(null);
  const chunks = sliceSnap.chunks;
  const base = sliceSnap.baseColumns.find(
    (c) => c.sourceColumn === snap.sourceColumn,
  );
  const manual = !base || base.targetField !== snap.targetField;
  const aiScore = scores?.get(snap.targetField);
  const effective = manual ? aiScore : snap.confidence;
  const warnBackground =
    effective !== undefined && mappingReviewConfidence(effective).warn;
  const refMeta = targetFields.find(
    (f) => f.id === snap.targetField,
  )?.reference;
  const rowLimit = sliceSnap.baseRows.length;
  const stats = useMemo(() => {
    if (!refMeta && !snap.resolution) return null;
    const keys = referenceKeyOccurrences({
      chunks,
      sourceColumn: snap.sourceColumn,
      cellOverrides: sliceSnap.cellOverrides,
      rejected: new Set(Object.keys(sliceSnap.rejectedRows).map(Number)),
      rowLimit,
    });
    const unresolved = [...keys.keys()].filter(
      (key) => !snap.resolution?.[key],
    ).length;
    return { distinct: keys.size, unresolved };
  }, [
    refMeta,
    chunks,
    snap.sourceColumn,
    snap.resolution,
    sliceSnap.cellOverrides,
    sliceSnap.rejectedRows,
    rowLimit,
  ]);
  const inScopeCells = useMemo(() => {
    const rejected = new Set(Object.keys(sliceSnap.rejectedRows).map(Number));
    return chunks
      .flatMap((c) => c.rows)
      .map((row, index) => ({ index, row }))
      .filter(({ index }) => index < rowLimit && !rejected.has(index))
      .map(({ index, row }) => {
        const override = sliceSnap.cellOverrides[index]?.[snap.sourceColumn];
        return override !== undefined ? override : row[snap.sourceColumn];
      });
  }, [
    chunks,
    snap.sourceColumn,
    sliceSnap.cellOverrides,
    sliceSnap.rejectedRows,
    rowLimit,
  ]);
  const sample =
    inScopeCells.find((v) => v != null && String(v).trim() !== '') ?? null;
  const diverged = base
    ? JSON.stringify({
        t: snap.targetField,
        r: snap.rules,
        res: snap.resolution,
      }) !==
      JSON.stringify({
        t: base.targetField,
        r: base.rules,
        res: base.resolution,
      })
    : false;
  const undoReason = submitting
    ? 'Saving — please wait'
    : !base
      ? `Not in the ${mappingReviewLabels.aiSuggestion}`
      : diverged
        ? `Reset to ${mappingReviewLabels.aiSuggestion}`
        : `Matches the ${mappingReviewLabels.aiSuggestion}`;
  const undoLabel =
    diverged && !submitting
      ? `Reset ${snap.sourceColumn} to ${mappingReviewLabels.aiSuggestion}`
      : `Reset ${snap.sourceColumn} to ${mappingReviewLabels.aiSuggestion}, unavailable: ${undoReason}`;

  const commitRules = () => {
    if (
      rulesDraft &&
      JSON.stringify(rulesDraft) !== JSON.stringify(snap.rules)
    ) {
      setColumnRules(slice.groupId, snap.sourceColumn, rulesDraft);
    }
    setRulesDraft(null);
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMN_GRID,
        columnGap: 10,
        rowGap: 5,
        alignItems: 'start',
        padding: 5,
        borderRadius: token.borderRadius,
        background: warnBackground ? token.colorWarningBg : undefined,
      }}
    >
      <Tooltip
        title={
          sample != null
            ? `${snap.sourceColumn} — e.g. ${String(sample)}`
            : snap.sourceColumn
        }
      >
        <Text>{snap.sourceColumn}</Text>
      </Tooltip>
      <MappingSelect
        target="fields"
        allowClear
        disabled={submitting}
        ariaLabel={`Map source column ${snap.sourceColumn}`}
        value={snap.targetField}
        suffix={refMeta ? <LinkOutlined /> : undefined}
        scored={[...(scores?.entries() ?? [])].map(([value, probability]) => ({
          value,
          probability,
        }))}
        fields={targetFields.map((f) => {
          const claimed = claimedBy.get(f.id);
          const other = claimed !== undefined && claimed !== snap.sourceColumn;
          return {
            value: f.id,
            label: other ? `${f.label} (mapped to "${claimed}")` : f.label,
            disabled: other,
          };
        })}
        onChange={(value) => {
          if (value) setColumnTarget(slice.groupId, snap.sourceColumn, value);
          else removeColumnMapping(slice.groupId, snap.sourceColumn);
        }}
      />
      <div>
        <MappingReviewConfidenceTag
          manual={manual}
          confidence={snap.confidence}
          aiScore={aiScore}
        />
      </div>
      <FlexBox noGrow gap={5} justifyStart wrap alignStart>
        {snap.rules.map((rule, index) => (
          <Tag key={`${rule.kind}-${index}`} style={{ margin: 0 }}>
            {mappingReviewLabels.rule[rule.kind]}
          </Tag>
        ))}
        <Popover
          trigger="click"
          open={rulesDraft !== null}
          onOpenChange={(next) => {
            if (next) setRulesDraft(snap.rules.map((r) => ({ ...r })));
            else setRulesDraft(null);
          }}
          title={`Cleaning rules — "${snap.sourceColumn}"`}
          content={
            rulesDraft && (
              <FlexCol
                noGrow
                gap={5}
                style={{
                  width: 280,
                  maxWidth: `calc(100vw - ${token.marginLG * 2}px)`,
                }}
              >
                <RulesEditor
                  sourceColumn={snap.sourceColumn}
                  rules={rulesDraft}
                  onChange={setRulesDraft}
                  cells={inScopeCells}
                  locale={sliceSnap.locale}
                  dateLocale={sliceSnap.dateLocale}
                  centuryPivot={sliceSnap.centuryPivot}
                  dateZone={sliceSnap.dateZone}
                  fieldDataTypeId={fieldDataTypeId}
                  percentScale={snap.percentScale}
                  options={fieldOptions}
                />
                <FlexBox noGrow gap={5} justifyEnd>
                  <Button
                    size="small"
                    onClick={() => setRulesDraft(null)}
                    aria-label={`Cancel cleaning-rule edits for ${snap.sourceColumn}`}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    disabled={submitting}
                    onClick={commitRules}
                    aria-label={`Save cleaning rules for ${snap.sourceColumn}`}
                  >
                    Save
                  </Button>
                </FlexBox>
              </FlexCol>
            )
          }
        >
          <Button
            type="text"
            size="small"
            disabled={submitting}
            icon={<SettingOutlined />}
            aria-label={`Edit cleaning rules for ${snap.sourceColumn}`}
          />
        </Popover>
      </FlexBox>
      <Tooltip title={undoReason}>
        <span style={{ display: 'inline-flex' }}>
          <Button
            type="text"
            size="small"
            icon={<UndoOutlined />}
            disabled={!diverged || submitting}
            onClick={() =>
              resetColumnToProposal(slice.groupId, snap.sourceColumn)
            }
            aria-label={undoLabel}
          />
        </span>
      </Tooltip>
      {(isPercentage || snap.detectedCurrency) && (
        <FlexBox
          noGrow
          gap={10}
          justifyStart
          wrap
          alignStart
          style={{ gridColumn: '1 / -1' }}
        >
          {snap.detectedCurrency && (
            <FlexBox noGrow gap={5} justifyStart>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                Currency
              </Text>
              <Tag style={{ margin: 0 }}>{snap.detectedCurrency}</Tag>
            </FlexBox>
          )}
          {isPercentage && (
            <FlexBox noGrow gap={5} justifyStart>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                Magnitude
              </Text>
              <Select
                size="small"
                aria-label={`Percentage magnitude convention for ${snap.sourceColumn}`}
                value={snap.percentScale ?? 'percent'}
                disabled={submitting}
                onChange={(value: 'percent' | 'fraction') =>
                  setColumnPercentScale(slice.groupId, snap.sourceColumn, value)
                }
                options={PERCENT_SCALE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                style={{ minWidth: 190 }}
              />
            </FlexBox>
          )}
        </FlexBox>
      )}
      {stats && (
        <FlexBox noGrow gap={5} justifyStart style={{ gridColumn: '1 / -1' }}>
          <Text
            type="secondary"
            style={{
              fontSize: token.fontSizeSM,
              color: stats.unresolved > 0 ? token.colorWarningText : undefined,
            }}
          >
            {stats.distinct - stats.unresolved}/{stats.distinct} values resolved
          </Text>
          <Button
            type="link"
            size="small"
            disabled={!refMeta}
            onClick={() =>
              onOpenReferenceValues({
                groupId: slice.groupId,
                sourceColumn: snap.sourceColumn,
                filter: stats.unresolved > 0 ? 'unresolved' : 'all',
              })
            }
            aria-label={`Resolve reference values for ${snap.sourceColumn}`}
          >
            Resolve
          </Button>
        </FlexBox>
      )}
    </div>
  );
};

const AddMappingButton: FC<{
  groupId: string;
  unmappedSources: string[];
  unclaimedTargets: TargetFieldOption[];
  scores: Map<string, Map<string, number>>;
}> = ({ groupId, unmappedSources, unclaimedTargets, scores }) => {
  const { token } = theme.useToken();
  const submitting = useMappingReviewSubmitting();
  const [source, setSource] = useState<string | undefined>(undefined);
  const [target, setTarget] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const sourceRow = source ? scores.get(source) : undefined;
  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      title="Add mapping"
      content={
        <FlexCol
          gap={5}
          style={{
            width: 240,
            maxWidth: `calc(100vw - ${token.marginLG * 2}px)`,
          }}
        >
          <Select
            size="small"
            showSearch
            placeholder="Source column"
            aria-label="Add mapping source column"
            value={source}
            options={unmappedSources.map((s) => ({ value: s, label: s }))}
            onChange={setSource}
          />
          <MappingSelect
            target="fields"
            placeholder="Target field"
            ariaLabel="Add mapping target field"
            value={target}
            scored={[...(sourceRow?.entries() ?? [])].map(
              ([value, probability]) => ({ value, probability }),
            )}
            fields={unclaimedTargets.map((f) => ({
              value: f.id,
              label: f.label,
            }))}
            onChange={setTarget}
          />
          <Button
            size="small"
            type="primary"
            disabled={!source || !target}
            aria-label="Confirm add mapping"
            onClick={() => {
              if (!source || !target) return;
              addColumnMapping(groupId, source, target);
              setSource(undefined);
              setTarget(undefined);
              setOpen(false);
            }}
          >
            Add
          </Button>
        </FlexCol>
      }
    >
      <Button
        size="small"
        disabled={submitting}
        icon={<PlusOutlined />}
        aria-label="Add mapping"
      >
        Add mapping
      </Button>
    </Popover>
  );
};

interface MappingReviewColumnsProps {
  slice: MappingReviewTableSlice;
  /** Target field options for the slice's destination (table-type columns or entity fields). */
  targetFields: TargetFieldOption[];
  fieldsLoading: boolean;
  referenceValues: ReferenceValuesTarget | null;
  onOpenReferenceValues: (target: ReferenceValuesTarget) => void;
  onCloseReferenceValues: () => void;
}

const MappingReviewColumns: FC<MappingReviewColumnsProps> = ({
  slice,
  targetFields: fields,
  fieldsLoading: loading,
  referenceValues,
  onOpenReferenceValues,
  onCloseReferenceValues,
}) => {
  const { token } = theme.useToken();
  const submitting = useMappingReviewSubmitting();
  const snap = useSnapshot(slice);
  const scores = useMemo(
    () => mappingReviewColumnScores(snap.baseColumns, snap.unmappedTargets),
    [snap.baseColumns, snap.unmappedTargets],
  );
  const sourceColumns = [
    ...new Set(snap.chunks.flatMap((c) => c.sourceColumns)),
  ];
  const claimedBy = new Map(
    snap.columns.map((c): [string, string] => [c.targetField, c.sourceColumn]),
  );
  const mappedSourceColumns = snap.columns.map((c) => c.sourceColumn);
  const unmappedSources = sourceColumns.filter(
    (s) => !mappedSourceColumns.includes(s),
  );
  const unclaimedTargets = fields.filter((f) => !claimedBy.has(f.id));
  const baseSources = new Set(snap.baseColumns.map((c) => c.sourceColumn));
  const drawerColumn = referenceValues
    ? snap.columns.find((c) => c.sourceColumn === referenceValues.sourceColumn)
    : undefined;
  const drawerMeta = drawerColumn
    ? fields.find((f) => f.id === drawerColumn.targetField)
    : undefined;

  return (
    <FlexCol
      noGrow
      gap={5}
      className="mapping-review-columns"
      data-group-id={slice.groupId}
    >
      <FlexBox noGrow justifyStart>
        <Title level={5} style={{ margin: 0 }}>
          Column mappings ({snap.columns.length}/{sourceColumns.length})
        </Title>
        <AddMappingButton
          groupId={slice.groupId}
          unmappedSources={unmappedSources}
          unclaimedTargets={unclaimedTargets}
          scores={scores}
        />
        {loading && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {mappingReviewLabels.loadingFields}
          </Text>
        )}
      </FlexBox>
      <div style={{ overflowX: 'auto', alignSelf: 'stretch' }}>
        <FlexCol noGrow noGap>
          <ColumnsHeader />
          {snap.columns.map((column, index) => (
            <ColumnRow
              key={column.sourceColumn}
              slice={slice}
              column={slice.columns[index]}
              targetFields={fields}
              claimedBy={claimedBy}
              scores={scores.get(column.sourceColumn)}
              onOpenReferenceValues={onOpenReferenceValues}
            />
          ))}
          {unmappedSources.map((sourceColumn) => (
            <div
              key={sourceColumn}
              style={{
                display: 'grid',
                gridTemplateColumns: COLUMN_GRID,
                columnGap: 10,
                alignItems: 'start',
                padding: 5,
                border: `1px dashed ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
              }}
            >
              <Text style={{ color: token.colorTextDisabled }}>
                {sourceColumn}
              </Text>
              <MappingSelect
                target="fields"
                disabled={submitting}
                placeholder={mappingReviewLabels.notMapped}
                ariaLabel={`Map source column ${sourceColumn}`}
                value={undefined}
                scored={[...(scores.get(sourceColumn)?.entries() ?? [])].map(
                  ([value, probability]) => ({ value, probability }),
                )}
                fields={unclaimedTargets.map((f) => ({
                  value: f.id,
                  label: f.label,
                }))}
                onChange={(value) => {
                  if (value)
                    addColumnMapping(slice.groupId, sourceColumn, value);
                }}
              />
              <span />
              <span />
              {baseSources.has(sourceColumn) ? (
                <Tooltip title={`Reset to ${mappingReviewLabels.aiSuggestion}`}>
                  <Button
                    type="text"
                    size="small"
                    disabled={submitting}
                    icon={<UndoOutlined />}
                    onClick={() =>
                      resetColumnToProposal(slice.groupId, sourceColumn)
                    }
                    aria-label={`Reset ${sourceColumn} to ${mappingReviewLabels.aiSuggestion}`}
                  />
                </Tooltip>
              ) : (
                <span />
              )}
            </div>
          ))}
        </FlexCol>
      </div>
      {referenceValues && drawerColumn && drawerMeta?.reference && (
        <MappingReviewReferenceValues
          slice={slice}
          sourceColumn={referenceValues.sourceColumn}
          entityId={drawerMeta.reference.entityId}
          entityGroupId={drawerMeta.reference.entityGroupId}
          targetLabel={drawerMeta.label}
          filter={referenceValues.filter}
          onFilterChange={(filter) =>
            onOpenReferenceValues({ ...referenceValues, filter })
          }
          onClose={onCloseReferenceValues}
        />
      )}
    </FlexCol>
  );
};

export default MappingReviewColumns;
